import { useCallback, useEffect } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Download, Lock } from "lucide-react";
import { Header } from "@/client/components/layout/header";
import { StoryWorkspace } from "@/client/components/layout/story-workspace";
import { Button } from "@/client/components/ui/button";
import { rememberLastStory, useStory } from "@/client/hooks/use-stories";
import { useStorySync } from "@/client/hooks/use-story-sync";
import { TabsProvider } from "@/client/hooks/use-tabs-context";
import { sessionUser } from "@/client/db";
import { downloadDrafts, listDrafts } from "@/client/db/drafts";

export const Route = createFileRoute("/story/$storyId")({
  // The open tab, like ?tab=scene-<id>, so Back moves between tabs and a scene can be linked to
  validateSearch: (search: Record<string, unknown>): { tab?: string } =>
    typeof search.tab === "string" ? { tab: search.tab } : {},
  component: StoryView,
});

function StoryView() {
  const { storyId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onActiveTabChange = useCallback(
    (tabId: string | null, mode: "push" | "replace") =>
      void navigate({ search: tabId ? { tab: tabId } : {}, replace: mode === "replace" }),
    [navigate]
  );
  const sync = useStorySync(storyId);
  const { data: story } = useStory(storyId);
  const found = !!story;

  useEffect(() => {
    if (found) rememberLastStory(storyId);
  }, [found, storyId]);

  if (sync.status === "access-lost") {
    return <AccessLost storyId={storyId} />;
  }

  if (sync.status === "error" && !story) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex h-[calc(100dvh-48px)] flex-col items-center justify-center gap-2 text-center">
          <p className="text-muted-foreground">Couldn't load this story.</p>
          <p className="text-sm text-muted-foreground">{sync.error}</p>
        </div>
      </div>
    );
  }

  if (sync.status === "loading" || !story) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center h-[calc(100dvh-48px)]">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <TabsProvider key={storyId} storyId={storyId} urlTabId={tab} onActiveTabChange={onActiveTabChange}>
      <StoryContent storyId={storyId} storyName={story.name} />
    </TabsProvider>
  );
}

/**
 * The story was deleted or the user was removed from it. Its cached data is
 * gone; only writing this user left unsaved on the device can be recovered,
 * and nothing new is fetched.
 */
function AccessLost({ storyId }: { storyId: string }) {
  const drafts = listDrafts(sessionUser().id, storyId);
  return (
    <div className="min-h-screen">
      <Header />
      <div className="flex h-[calc(100dvh-48px)] flex-col items-center justify-center gap-3 px-4 text-center">
        <Lock className="h-10 w-10 text-muted-foreground/50" />
        <h1 className="text-xl font-semibold">You no longer have access to this story</h1>
        <p className="max-w-md text-muted-foreground">
          It was deleted, or its owner removed you.
          {drafts.length > 0 &&
            ` You have unsaved writing from ${drafts.length === 1 ? "one scene" : `${drafts.length} scenes`} on this device.`}
        </p>
        <div className="flex gap-2">
          {drafts.length > 0 && (
            <Button variant="outline" onClick={() => downloadDrafts(drafts)}>
              <Download />
              Download unsaved writing
            </Button>
          )}
          <Button asChild>
            <Link to="/">Back to your stories</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

interface StoryContentProps {
  storyId: string;
  storyName: string;
}

function StoryContent({ storyId, storyName }: StoryContentProps) {
  return (
    // Fills the app's dynamic-height frame, so the phone's bottom bar stays on screen
    <div className="h-full flex flex-col overflow-hidden">
      <Header storyName={storyName} storyId={storyId} />
      <StoryWorkspace storyId={storyId} />
    </div>
  );
}
